import {
  Grid,
  Skeleton,
  Table as MaUTable,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  Box,
  Divider,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { FC, ReactNode, useContext, useEffect } from 'react';
import {
  Cell,
  ColumnInterface,
  Row,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
  UseTableOptions,
} from 'react-table';
import { HeaderContext } from '../../context';
import { formatPercent, getLocalDate, getLocalDateTime } from '../../helpers';
import { FullPagination } from './Pagination';
import { ITotalsRowProps } from '../../models/table';

// `ITableColumn` is our version of `Column`, or rather, `ColumnInterface`
export interface IFullTableColumn<D extends object = {}> extends ColumnInterface<D> {
  accessor?: ((item: Row<D>) => string | number) | string;
  canFilter?: boolean;
  className?: string;
  columnAlignment?: TableCellProps['align'];
  filterType?: 'input' | 'autocomplete';
  handleClickColumn?: (columnId: any) => void;
  hideLoad?: boolean;
  isCentered?: boolean;
  isCurrency?: boolean;
  isDate?: boolean;
  isDateTime?: boolean;
  isNumber?: boolean;
  isPercent?: boolean;
  isServerSorted?: boolean;
  isServerSortedDesc?: boolean;
  overrideWidth?: number;
  sort?: boolean;
}

interface IFullTable<D extends object = {}> extends Pick<UseTableOptions<D>, 'data' | 'columns'> {
  cellClasses?: string | ((original: D) => string);
  centerPagination?: boolean;
  containerClasses?: string;
  handlePage?: (val: number) => void;
  handleRowsPerPage?: (val: number) => void;
  headerClasses?: string;
  hideDeleted?: boolean;
  hidePagination?: boolean;
  isLoading?: boolean;
  loadingPageSize?: number;
  LoadMoreComponent?: FC;
  mobileProps?: Record<string, unknown>;
  noResultsText?: string;
  onRowsPerPageChange?: (rows: number) => void;
  ResponsiveComponent?: FC<any>;
  ResponsiveComponentLoader?: FC;
  rowClasses?: string | ((original: D) => string);
  rowOnClick?: (val: unknown) => void;
  serverPage?: number;
  serverRecordCount?: number;
  serverPerPage?: number;
  stickyHeader?: boolean;
  tableSize?: 'medium' | 'small';
  useTableProps?: UseTableOptions<D>;
  Cell?: (data: any) => JSX.Element;
  rowsPerPageOptions?: number[];
  totalsRow?: ITotalsRowProps;
}

const currencyRE = /\B(?=(\d{3})+(?!\d))/g;

const formatCell = (cell: Cell<{}>): ReactNode => {
  const tColumn = cell.column as IFullTableColumn; // ColumnInstance with extra properties

  if (tColumn.isDate) return getLocalDate(cell.value);
  if (tColumn.isDateTime) return getLocalDateTime(cell.value);
  else if (tColumn.isCurrency) return `$${Number(cell.value).toFixed(2).replace(currencyRE, ',')}`;
  else if (tColumn.isPercent) return formatPercent(cell.value);
  else return cell.render('Cell');
};

const getCellStyle = (column: IFullTableColumn, type: 'header' | 'cell'): React.CSSProperties => {
  let style: React.CSSProperties = { cursor: 'inherit' };

  if (column.isDate) {
    style = {
      ...style,
    };
  } else if (column.isNumber) {
    style = {
      ...style,
      textAlign: 'right',
      paddingRight: '3rem',
    };
  } else if (column.isCurrency) {
    style = {
      ...style,
    };
  } else if (column.isCentered) {
    style = {
      ...style,
      textAlign: 'center',
    };
  }

  if (column.overrideWidth) {
    style = {
      ...style,
      width: column.overrideWidth,
      maxWidth: column.overrideWidth,
      wordWrap: 'break-word', // IE11
      overflowWrap: 'break-word',
    };
  }

  if (type === 'header') {
    style = {
      ...style,
      fontWeight: 'bold',
      cursor: column.handleClickColumn ? 'pointer' : 'inherit',
    };
  }

  return style;
};

export const FullTable: FC<IFullTable> = ({
  cellClasses = '',
  centerPagination,
  columns,
  containerClasses = '',
  data,
  headerClasses,
  hideDeleted,
  hidePagination,
  isLoading,
  loadingPageSize,
  LoadMoreComponent,
  mobileProps,
  noResultsText = 'No Results',
  onRowsPerPageChange,
  ResponsiveComponent,
  ResponsiveComponentLoader,
  rowClasses,
  rowOnClick,
  serverPage,
  serverRecordCount,
  serverPerPage,
  handlePage,
  handleRowsPerPage,
  stickyHeader = false,
  tableSize = 'small',
  useTableProps = {},
  rowsPerPageOptions,
  totalsRow,
}) => {
  const { headerHeight } = useContext(HeaderContext);
  const classes = useStyles({ headerHeight });
  const isDesktop = useMediaQuery('(min-width: 960px)');

  const {
    getTableProps,
    headerGroups,
    prepareRow,
    // @ts-ignore
    page,
    // @ts-ignore
    gotoPage,
    // @ts-ignore
    setPageSize,
    // @ts-ignore
    state: { pageSize },
  } = useTable(
    {
      columns,
      data,
      // @ts-ignore
      userPageCount: 1,
      manualPagination: hidePagination,
      autoResetPage: isLoading,
      autoResetSortBy: isLoading,
      ...useTableProps,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const setPage = (page: number) => {
    gotoPage(page);
  };

  // update page size if we hide pagination
  useEffect(() => {
    data.length ? setPageSize(Number(data.length)) : setPageSize(Number(10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // always start on first page if data length changes, ie; filter was applied
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.length]);
  return (
    <>
      <Box
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          overflow: 'unset',
          position: 'relative',
          minHeight: 100,
          '& table': {
            width: '100%',
            tableLayout: 'auto',
          },
          '& th:first-of-type, & td:first-of-type': {
            minWidth: '100px !important',
            width: '200px !important',
            maxWidth: '300px !important',
            paddingLeft: '20px !important',
            textAlign: 'left !important',
          },
        }}
      >
        <TableContainer className={clsx(containerClasses, stickyHeader ? classes.stickyHeader : '')}>
          <MaUTable
            stickyHeader={stickyHeader}
            size={tableSize}
            {...getTableProps()}
            style={ResponsiveComponent && !isLoading ? { display: 'block' } : undefined}
          >
            {isDesktop || !ResponsiveComponent ? (
              <>
                {/** TABLE HEADER */}
                <TableHead
                  className={clsx(stickyHeader ? classes.stickyTableHeader : '')}
                  sx={{
                    '& tr': {
                      borderRadius: '2px',
                      '& th': {
                        fontWeight: 'bold',
                        backgroundColor: theme => theme.frankie.searchBlue,
                        // Nearly white, tinted towards the themefied Frankie searchBlue color
                        color: '#eff4f6',
                      },
                    },
                  }}
                >
                  {headerGroups.map(headerGroup => (
                    <TableRow {...headerGroup.getHeaderGroupProps()}>
                      {/** TABLE HEADER COLUMNS */}
                      {headerGroup.headers.map((column: any) => (
                        <TableCell
                          align={column?.columnAlignment ?? column?.isCurrency ? 'right' : 'left'}
                          {...((column as IFullTableColumn).sort !== false
                            ? column.getHeaderProps(column.getSortByToggleProps())
                            : column.getHeaderProps())}
                          title={column.title || ''}
                          className={headerClasses}
                          style={getCellStyle(column, 'header')}
                          onClick={e => {
                            const { handleClickColumn, sort } = column as IFullTableColumn;
                            const sortProps: any = column.getHeaderProps(
                              column.getSortByToggleProps()
                            );

                            // If the column is sortable and there's an onClick handler, then call it
                            const { onClick } = sortProps || {};
                            sort !== false && onClick && onClick(e);

                            // Also run column click handler if passed
                            handleClickColumn && handleClickColumn(column.id);
                          }}
                        >
                          {(column as IFullTableColumn).sort !== false ||
                          (column as IFullTableColumn).isServerSorted ? (
                            <TableSortLabel
                              active={column.isSorted || (column as IFullTableColumn).isServerSorted}
                              direction={
                                column.isSortedDesc || (column as IFullTableColumn).isServerSortedDesc
                                  ? 'desc'
                                  : 'asc'
                              }
                            >
                              {column.render('Header')}
                            </TableSortLabel>
                          ) : (
                            column.render('Header')
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>

                {/** TABLE BODY ROWS */}
                <TableBody>
                  {isLoading
                    ? new Array(loadingPageSize || pageSize || 3).fill('').map((_, j) => (
                        <TableRow
                          key={`table-row-skeleton-${j}`}
                          {...(rowOnClick ? { onClick: rowOnClick } : {})}
                        >
                          {/** LOADING SKELETON */}
                          {columns.map((column: any, i) => (
                            <TableCell
                              align={column?.columnAlignment ?? 'left'}
                              key={`skeleton-cell-${i}`}
                              style={getCellStyle(column as IFullTableColumn, 'cell')}
                              className={
                                typeof cellClasses === 'function' ? cellClasses({}) : cellClasses
                              }
                            >
                              {column.hideLoad ? null : <Skeleton />}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : page.map((row: Row) => {
                        prepareRow(row);
                        return !hideDeleted || (hideDeleted && !(row.original as any).isDeleted) ? (
                          <TableRow
                            {...row.getRowProps()}
                            className={
                              typeof rowClasses === 'function' ? rowClasses(row.original) : rowClasses
                            }
                            {...(rowOnClick ? { onClick: () => rowOnClick(row.original) } : {})}
                          >
                            {row.cells.map(cell => {
                              const tableColumn: IFullTableColumn | undefined = cell?.column;
                              return (
                                <TableCell
                                  align={
                                    tableColumn?.columnAlignment || tableColumn?.isCurrency
                                      ? 'right'
                                      : 'left'
                                  }
                                  {...cell.getCellProps()}
                                  className={clsx(
                                    tableColumn?.className ?? '',
                                    typeof cellClasses === 'function'
                                      ? cellClasses(row.original)
                                      : cellClasses,
                                    tableColumn?.className ?? ''
                                  )}
                                  style={getCellStyle(tableColumn, 'cell')}
                                >
                                  {formatCell(cell)}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ) : null;
                    }
                    )}
                  {totalsRow ?
                    // TODO: Make the total area more reusable
                    <>
                      <TableRow>
                      <TableCell colSpan={columns.length} style={{ padding: 0, border: 0 }}>
                        <Divider />
                      </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={totalsRow.titleColSpan}><strong>{totalsRow.title}</strong></TableCell>
                        {totalsRow.columns.map((totalsCol, index) => (
                          <TableCell align={totalsCol?.isCurrency ? 'right' : 'left'} key={`totals-col-${index}`} colSpan={1}>
                            {totalsCol.isCurrency ? `$${Number(totalsCol.value).toFixed(2).replace(currencyRE, ',')}` : totalsCol.value}
                          </TableCell>
                        ))}
                      </TableRow>
                    </> : null}
                </TableBody>
              </>
            ) : (
              <TableBody className={isLoading ? undefined : classes.mobileTable}>
                {/** TABLE BODY FROM RESPONSIVE COMPONENT */}
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      className={clsx(
                        classes.mobileCell,
                        typeof cellClasses === 'function' ? cellClasses({}) : cellClasses
                      )}
                    >
                      {ResponsiveComponentLoader ? <ResponsiveComponentLoader /> : <Skeleton />}
                    </TableCell>
                  </TableRow>
                ) : (
                  (page as Row[]).map((row, i) => {
                    prepareRow(row);
                    return !hideDeleted || (hideDeleted && !(row.original as any).isDeleted) ? (
                      <TableRow
                        {...row.getRowProps()}
                        className={
                          typeof rowClasses === 'function' ? rowClasses(row.original) : rowClasses
                        }
                        {...(rowOnClick ? { onClick: () => rowOnClick(row.original) } : {})}
                        style={{ display: 'block' }}
                      >
                        <TableCell
                          key={`row-${i}-mobile-cell`}
                          className={clsx(
                            classes.mobileCell,
                            typeof cellClasses === 'function'
                              ? cellClasses(row.original)
                              : cellClasses
                          )}
                          style={{ display: 'block' }}
                        >
                          <ResponsiveComponent
                            key={`responsive-row-${i}`}
                            {...row}
                            {...useTableProps}
                            {...mobileProps}
                          />
                        </TableCell>
                      </TableRow>
                    ) : null;
                  })
                )}
              </TableBody>
            )}
          </MaUTable>

          {/** NO RESULTS DISPLAY */}
          {!isLoading && data.length === 0 && (
            <Grid container justifyContent="center">
              <Typography className={classes.noResults} variant="body1" gutterBottom>
                {noResultsText}
              </Typography>
            </Grid>
          )}

          {LoadMoreComponent && <LoadMoreComponent />}
        </TableContainer>
      </Box>

      {!isLoading &&
        data.length > 0 &&
        !hidePagination &&
        !isNaN(serverPage as number) &&
        !isNaN(serverPerPage as number) &&
        !isNaN(serverRecordCount as number) &&
        handlePage &&
        handleRowsPerPage && (
          <div className={classes.centerPagination}>
            <FullPagination
              count={serverRecordCount as number}
              rowsPerPage={serverPerPage as number}
              page={serverPage as number}
              setPage={handlePage}
              setRowsPerPage={handleRowsPerPage}
              rowsPerPageOptions={rowsPerPageOptions}
            />
          </div>
        )}
    </>
  );
};

const useStyles = makeStyles<Theme, { headerHeight: number }>(theme => ({
  noResults: {
    '& p': {
      marginTop: '1rem',
    },
  },
  stickyHeader: {
    flexGrow: 1,
    maxHeight: '100%',
    overflowX: 'initial',
  },
  stickyTableHeader: {
    position: 'sticky',
    top: ({ headerHeight }) => headerHeight + 'px',
    zIndex: 2,
  },
  borderNone: {
    border: 'none',
  },
  mobileTable: {
    display: 'block',
  },
  mobileCell: {
    padding: 0,
    border: 0,
  },
  centerPagination: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));
